import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  addLink,
  defaultLayout,
  migrateCustomNav,
  normalizeLayout,
  type LegacyCustomNavItem,
  type RoleGroup,
  type SidebarLayout,
} from '~/components/Sidebar/sidebarLayout';

function storageKey(userId: string | null | undefined, roleGroup: RoleGroup | null) {
  return `solastack.sidebarLayout.v2.${userId || 'anon'}.${roleGroup || 'none'}`;
}

/** The folder-era layout, read once so its saved views are not lost with it. */
function foldersKey(userId: string | null | undefined, roleGroup: RoleGroup | null) {
  return `solastack.sidebarLayout.v1.${userId || 'anon'}.${roleGroup || 'none'}`;
}

function legacyKey(userId: string | null | undefined) {
  return `solastack.customNavigation.v1.${userId || 'anon'}`;
}

function readJson<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function loadLayout(userId: string | null | undefined, roleGroup: RoleGroup | null): SidebarLayout {
  const raw = readJson<SidebarLayout>(storageKey(userId, roleGroup));
  const stored = normalizeLayout(raw);
  if (stored) return adoptCurrentDefaults(raw, stored);

  // First visit since the folders went. The links are reseeded from the role's
  // default rather than carried over, because what the old layout mostly
  // recorded was which folder things sat in, and there are no folders to sit
  // in. Saved views are the part the user actually made, so those come across,
  // along with anything still sitting in the older "custom navigation" list.
  const folderEra = normalizeLayout(readJson(foldersKey(userId, roleGroup)));
  const views = folderEra?.entries.filter((entry) => entry.type === 'view') ?? [];
  const base = defaultLayout(roleGroup);
  const withViews: SidebarLayout = { ...base, entries: [...base.entries, ...views] };

  const legacy = readJson<LegacyCustomNavItem[]>(legacyKey(userId));
  return migrateCustomNav(withViews, Array.isArray(legacy) ? legacy : []);
}

/**
 * A stored layout from before Attendance existed should gain that link once.
 * Version 3 arrangements are left alone, so hiding the link stays hidden.
 */
function adoptCurrentDefaults(raw: unknown, layout: SidebarLayout): SidebarLayout {
  const version = raw && typeof raw === 'object' && typeof (raw as { version?: unknown }).version === 'number'
    ? (raw as { version: number }).version
    : 0;
  if (version >= 3) return layout;
  const scheduleIndex = layout.entries.findIndex((entry) => entry.type === 'link' && entry.key === 'schedule');
  return addLink(layout, 'attendance', scheduleIndex === -1 ? layout.entries.length : scheduleIndex + 1);
}

/**
 * The signed-in user's sidebar arrangement for their current role, kept in
 * localStorage (per user, per role group) and synced across tabs.
 *
 * Roles that cannot customize (teachers) always get their role's default. That
 * matters for more than tidiness: a stored arrangement they have no way to edit
 * would be a sidebar they are stuck with, including any saved views from before
 * the customizer was taken away from them.
 */
export function useSidebarLayout(
  userId: string | null | undefined,
  roleGroup: RoleGroup | null,
  customizable = true
) {
  const key = useMemo(() => storageKey(userId, roleGroup), [userId, roleGroup]);
  const [layout, setLayoutState] = useState<SidebarLayout>(
    () => (customizable ? loadLayout(userId, roleGroup) : defaultLayout(roleGroup))
  );

  useEffect(() => {
    setLayoutState(customizable ? loadLayout(userId, roleGroup) : defaultLayout(roleGroup));
  }, [userId, roleGroup, customizable]);

  const setLayout = useCallback((next: SidebarLayout) => {
    setLayoutState(next);
    try {
      window.localStorage.setItem(key, JSON.stringify(next));
    } catch {
      // Storage can be unavailable (private mode); the in-memory layout still works for this session.
    }
  }, [key]);

  useEffect(() => {
    if (!customizable) return undefined;
    const handler = (event: StorageEvent) => {
      if (event.key !== key) return;
      const next = normalizeLayout(event.newValue ? JSON.parse(event.newValue) : null);
      if (next) setLayoutState(next);
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [key, customizable]);

  const resetLayout = useCallback(() => setLayout(defaultLayout(roleGroup)), [roleGroup, setLayout]);

  return { layout, setLayout, resetLayout };
}
